import { type SxProps, Typography } from '@mui/material';
import { useSession } from '@side/next-auth/react';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { useMemo } from 'react';
import {
  type CompensationReportFiltersFormValues,
  useCompensationReportFilters,
} from '../CompensationReportFilters/CompensationReportFilters.helpers';
import { isReportingPeriodCustom, isReportingPeriodL365 } from '../helpers';
import { usePerformanceReportContext } from '../PerformanceReport/context/PerformanceReportContext';
import type { PerformanceReportFiltersFormValues } from '../PerformanceReport/types';

const CURRENT_YEAR = new Date().getFullYear();

function formatShortDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return format(new Date(year, month - 1, day), 'M/d/yy');
}

function formatCustomPeriodLabel(
  startDate: string | undefined,
  endDate: string | undefined,
): string {
  if (!startDate) return 'Custom';
  const startLabel = formatShortDate(startDate);
  if (!endDate) return startLabel;
  return `${startLabel} to ${formatShortDate(endDate)}`;
}

function useSharedFilterValues(
  filters:
    | CompensationReportFiltersFormValues
    | PerformanceReportFiltersFormValues
    | undefined,
) {
  const { data: session } = useSession();

  return useMemo(
    () => ({
      reportingPeriodLabel: (() => {
        if (!filters) return undefined;
        const period = filters.reportingPeriod;
        if (isReportingPeriodL365(period)) return 'Last 365 Days';
        if (isReportingPeriodCustom(period)) {
          return formatCustomPeriodLabel(
            filters.customDateStart,
            filters.customDateEnd,
          );
        }
        return period === CURRENT_YEAR ? `YTD ${period}` : `Year ${period}`;
      })(),
      teamsLabel:
        filters?.teams.length === 0
          ? 'All Teams'
          : filters?.teams
              .map((t: { label: string; value: string }) => t.label)
              .join(', '),
      agentsLabel:
        filters?.agents.length === 0
          ? 'All Agents'
          : filters?.agents
              .map((a) =>
                a.value === session?.user?.id ? `${a.label} (Me)` : a.label,
              )
              .filter(Boolean)
              .join(', '),
      hideTerminatedAgentsLabel: filters?.hideTerminatedAgents
        ? 'No Terminated Agents'
        : 'Terminated Agents',
    }),
    [filters, session],
  );
}

function getLeadSourcesLabel(leadSources: { label: string; value: string }[]) {
  return leadSources.length === 0
    ? 'All Lead Sources'
    : leadSources.map((s) => s.label).join(', ');
}

function useCompensationFilterValues() {
  const { filters } = useCompensationReportFilters();
  const {
    reportingPeriodLabel,
    teamsLabel,
    agentsLabel,
    hideTerminatedAgentsLabel,
  } = useSharedFilterValues(filters);

  const transactionTypeLabel =
    filters?.transactionType.length === 0
      ? 'All Transaction Types'
      : filters?.transactionType.map((t) => t).join(', ');

  const leadSourcesLabel = getLeadSourcesLabel(filters?.leadSources ?? []);

  return [
    'Closed',
    hideTerminatedAgentsLabel,
    reportingPeriodLabel,
    transactionTypeLabel,
    leadSourcesLabel,
    teamsLabel,
    agentsLabel,
  ];
}

export function usePerformanceFilterValues() {
  const { filters } = usePerformanceReportContext();
  const {
    reportingPeriodLabel,
    teamsLabel,
    agentsLabel,
    hideTerminatedAgentsLabel,
  } = useSharedFilterValues(filters);

  const statusLabel =
    filters?.status.length === 0
      ? 'All Statuses'
      : filters?.status.map((s) => upperFirst(s)).join(', ');

  const leadSourcesLabel = getLeadSourcesLabel(filters?.leadSources ?? []);

  return [
    statusLabel,
    leadSourcesLabel,
    reportingPeriodLabel,
    hideTerminatedAgentsLabel,
    teamsLabel,
    agentsLabel,
  ];
}

export function FilterValueContent({
  parts,
  sx,
}: {
  parts: (string | undefined)[];
  sx?: SxProps;
}) {
  return (
    <Typography
      variant="body3"
      sx={{
        textAlign: 'left',
        textOverflow: 'ellipsis',
        color: 'text.secondary',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        ml: 'auto',
        width: 'fit-content',
        maxWidth: '100%',
        ...sx,
      }}
    >
      Showing: {parts?.filter(Boolean).join(', ')}
    </Typography>
  );
}

export function CompensationFilterValuesFooter({ sx }: { sx?: SxProps }) {
  const parts = useCompensationFilterValues();
  return <FilterValueContent parts={parts} sx={sx} />;
}

export function PerformanceFilterValuesFooter({ sx }: { sx?: SxProps }) {
  const parts = usePerformanceFilterValues();
  return <FilterValueContent parts={parts} sx={sx} />;
}

export function FilterValuesFooter({
  variant,
  sx,
}: {
  variant: 'compensation' | 'performance';
  sx?: SxProps;
}) {
  switch (variant) {
    case 'compensation':
      return <CompensationFilterValuesFooter sx={sx} />;
    case 'performance':
      return <PerformanceFilterValuesFooter sx={sx} />;
    default:
      return null;
  }
}
